import { useState } from "react";
import { Check, Copy } from "lucide-react";

/**
 * Copy-to-clipboard with a confirmation state.
 *
 * The confirmation matters more than it looks: without it people click twice,
 * unsure whether the first click registered, and the second click is the one
 * that feels broken.
 */
export function CopyButton({ value, label = "Copy" }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard access is denied on insecure origins and in some embedded
      // browsers. Failing silently is right here — the text is on screen and
      // selectable, so the user has a fallback either way.
    }
  }

  return (
    <button
      onClick={handleCopy}
      aria-label={copied ? "Copied" : label}
      className="flex shrink-0 items-center gap-1.5 rounded-md border border-ink-700 bg-ink-800 px-2.5 py-1.5 text-xs text-ink-400 transition-colors hover:border-ink-400 hover:text-ink-50"
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? "Copied" : label}
    </button>
  );
}
